// Shared Google Maps API loader — deduplicates script loading AND detects the
// failure modes that should trigger our open-source (Leaflet/OSM) fallback:
//   1. No API key configured
//   2. Script fails to load (network/blocked)
//   3. Auth failure at render time — bad key, referrer/domain not allowed,
//      billing disabled — which Google reports via the global `gm_authFailure`
//      callback (NOT via the script onload), so we install it here.
//
// Consumers call loadGoogleMapsScript(); if it rejects, or googleMapsFailed()
// becomes true, render the Leaflet fallback instead.

let loadPromise: Promise<void> | null = null;
let authFailed = false;
const failListeners = new Set<() => void>();

/**
 * Mark Google Maps as failed and notify subscribers. Exported so consumers
 * (e.g. useGoogleMap2D) can flag failures the loader can't see itself, like
 * `google.maps.Map` construction throwing after the script loaded.
 */
export function markGoogleMapsFailed(): void {
  if (authFailed) return;
  authFailed = true;
  failListeners.forEach((cb) => { try { cb(); } catch { /* ignore */ } });
}

// Install the global auth-failure hook once. Google calls window.gm_authFailure
// when the key is rejected (RefererNotAllowed/InvalidKey/billing disabled).
function installAuthFailureHook() {
  if (typeof window === 'undefined') return;
  if ((window as any).__gmAuthHookInstalled) return;
  (window as any).__gmAuthHookInstalled = true;
  (window as any).gm_authFailure = () => {
    markGoogleMapsFailed();
  };
}

/** True once Google Maps has failed auth (bad key / referrer / billing). */
export function googleMapsFailed(): boolean {
  return authFailed;
}

/** Subscribe to the auth-failure event; returns an unsubscribe fn. */
export function onGoogleMapsFailure(cb: () => void): () => void {
  failListeners.add(cb);
  if (authFailed) cb(); // already failed → notify immediately
  return () => failListeners.delete(cb);
}

export function loadGoogleMapsScript(apiKey: string): Promise<void> {
  if (!apiKey) {
    // Missing key is a hard failure — trigger the OSM fallback listeners too.
    markGoogleMapsFailed();
    return Promise.reject(new Error('No Google Maps API key'));
  }
  installAuthFailureHook();
  if (typeof window.google !== 'undefined') return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=visualization,marker`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null;
      // Script blocked / network failure — same consumer behavior as an auth
      // failure: flip the flag and fire listeners so the OSM fallback renders.
      markGoogleMapsFailed();
      reject(new Error('Failed to load Google Maps script'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}
